import React, { useState } from 'react';
import { DayOfWeek, DAY_ABBREVIATIONS, FULL_DAY_NAMES, CustomDoseDay } from '../constants/days';

interface DoseCalendarProps {
  frequency?: number; // n - the interval between doses (for regular frequency mode)
  dosage?: number; // the dose amount in mg (for regular frequency mode)
  startingDay?: DayOfWeek; // optional starting day of the week (for external control)
  customDoseDays?: CustomDoseDay[]; // for twice-weekly or custom schedules
  showOneWeek?: boolean; // show exactly one week instead of multiple cycles
  showStartingDaySelector?: boolean; // show the starting day selector
}

/**
 * DoseCalendar component displays a visual calendar showing the dosing schedule
 * Can show either regular frequency-based dosing or custom twice-weekly schedules
 * If customDoseDays is provided, it takes precedence over frequency/dosage
 */
const DoseCalendar: React.FC<DoseCalendarProps> = ({
  frequency,
  dosage,
  startingDay: externalStartingDay,
  customDoseDays,
  showOneWeek = false,
  showStartingDaySelector = false,
}) => {
  // Internal state for starting day when selector is shown
  const [internalStartingDay, setInternalStartingDay] = useState<DayOfWeek | undefined>(undefined);

  // Use external starting day if provided, otherwise use internal state
  const startingDay = externalStartingDay ?? internalStartingDay;

  // Determine if we're using custom dose days (twice weekly) or regular frequency
  const isCustomSchedule = customDoseDays && customDoseDays.length > 0;

  // Calculate weekly equivalent dose
  const weeklyEquivalent = isCustomSchedule
    ? customDoseDays!.reduce((sum, doseDay) => sum + doseDay.dose, 0)
    : frequency && dosage
      ? (dosage * 7) / frequency
      : 0;

  // For custom schedules, show one week; for regular frequency, show enough to demonstrate pattern
  const totalDays = showOneWeek || isCustomSchedule ? 7 : (frequency || 1) * 2 + 1;
  const totalBoxes = showOneWeek || isCustomSchedule ? 7 : totalDays + 1;

  const getDayLabel = (dayNumber: number): string => {
    if (startingDay !== undefined) {
      // Calculate which day of the week this is, starting from the provided startingDay
      const dayIndex = (startingDay + dayNumber - 1) % 7;
      return DAY_ABBREVIATIONS[dayIndex as DayOfWeek];
    }
    return `Day ${dayNumber}`;
  };

  const getDoseForDay = (dayNumber: number): number | null => {
    if (isCustomSchedule) {
      // For custom schedules, check if this day matches any of the custom dose days
      const currentDayOfWeek = (startingDay! + dayNumber - 1) % 7;
      const doseDay = customDoseDays!.find(d => d.day === currentDayOfWeek);
      return doseDay ? doseDay.dose : null;
    } else {
      // Regular frequency-based dosing
      const doseDays = [1, (frequency || 1) + 1, (frequency || 1) * 2 + 1];
      return doseDays.includes(dayNumber) ? dosage || 0 : null;
    }
  };

  const renderDay = (dayNumber: number) => {
    // For non-custom schedules, show ellipsis on the last box
    if (!isCustomSchedule && !showOneWeek && dayNumber === totalBoxes) {
      return (
        <div key={dayNumber} className="calendar-day ellipsis-day">
          ...
        </div>
      );
    }

    // Don't show days beyond our calculation for regular frequency (except the ellipsis)
    if (!isCustomSchedule && !showOneWeek && dayNumber > totalDays) {
      return (
        <div key={dayNumber} className="calendar-day no-dose-day">
          <div className="day-number">{getDayLabel(dayNumber)}</div>
        </div>
      );
    }

    const doseAmount = getDoseForDay(dayNumber);
    const isDoseDay = doseAmount !== null;

    return (
      <div key={dayNumber} className={`calendar-day ${isDoseDay ? 'dose-day' : 'no-dose-day'}`}>
        <div className="day-number">{getDayLabel(dayNumber)}</div>
        {isDoseDay && <div className="dose-amount">{doseAmount!.toFixed(2)}mg</div>}
      </div>
    );
  };

  return (
    <div className="dose-calendar-container">
      <div className="calendar-wrapper">
        <h3 style={{ textAlign: 'center', marginTop: '0px', marginBottom: '15px', color: '#252441' }}>
          Dosing Schedule for {weeklyEquivalent.toFixed(2)}mg Weekly Equivalent
        </h3>
        <div className="calendar-grid">{Array.from({ length: totalBoxes }, (_, index) => renderDay(index + 1))}</div>

        {showStartingDaySelector && (
          <div style={{ marginTop: '15px', marginBottom: '15px', textAlign: 'center' }}>
            <label htmlFor="starting-day-select" style={{ marginRight: '10px', fontWeight: 'bold' }}>
              Choose your starting day:
            </label>
            <select
              id="starting-day-select"
              value={internalStartingDay ?? ''}
              onChange={e =>
                setInternalStartingDay(e.target.value ? (parseInt(e.target.value) as DayOfWeek) : undefined)
              }
              style={{
                padding: '8px 12px',
                border: '2px solid #252441',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: '#252441',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              <option value="">"Day 1" - Generic</option>
              {Object.entries(FULL_DAY_NAMES).map(([dayValue, dayName]) => (
                <option key={dayValue} value={dayValue}>
                  {dayName}
                </option>
              ))}
            </select>
          </div>
        )}

        {!isCustomSchedule && !showOneWeek && (
          <div className="pattern-continues">Pattern continues every {frequency} days...</div>
        )}

        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-box dose-day"></div>
            <span>Dose day</span>
          </div>
          <div className="legend-item">
            <div className="legend-box no-dose-day"></div>
            <span>No dose</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoseCalendar;
